/**
 * 承認の対象の種別と、担当者かの照合
 * （`getApprovalAccess`）の入出力スキーマ。
 *
 * 用語・ステージ・アクターの新設・更新・削除は誰でも提案でき、案件の変更に
 * 積まれる。**承認はコンソールの担当者の操作だけ**で、MCP には出さない — 書き手（AI を含む）が
 * 自分の提案を自分で承認できると、業務名の主語になる用語や業務の分類になるステージ・
 * アクターごと分身業務を作れてしまう。
 * 未承認の変更が残る案件は確定できない（制約 31）。
 *
 * 承認の書込と承認の一覧の入出力は作り直しの契約（`elements/pj-operations.ts` /
 * `elements/approvals.ts`）が持つ。旧表を相手にした一覧と承認（撤去済み）の判断の値と件数の
 * 上限（`TermApprovalDecisionSchema` / `TERM_APPROVAL_BATCH_LIMIT`）は、画面のテストが参照して
 * いるあいだ残している。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 承認の対象の種別と、呼び出した本人が承認の担当者かを返す照合の入出力契約
 * @graph-connects none
 */
import { TERM_APPROVAL_DECISIONS } from '@cortex/domain-spec-graph';
import { z } from 'zod';
/** 承認の判断（承認 / 却下）。共通部の allowlist を写す。 @graph-connects none */
export const TermApprovalDecisionSchema = z.enum(TERM_APPROVAL_DECISIONS);
/**
 * 承認の対象になるデータ種別。共通部の承認が要るノード型と 1 対 1 で対応する。
 *
 * 種別を列で持つのは、承認の対象が広がったときに一覧の型を作り直さないためである（用語だけ
 * だった頃から列で持ち、種別を足しても表の形は変えていない）。管理者は PJ と種別を跨いで判断
 * するので、種別が増えても「1 表に並べて絞る」形は変わらない。値を増やすときはこの allowlist と
 * `APPROVAL_KIND_BY_NODE_TYPE` に足し、行の `kind` で描き分ける。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 承認の対象になるデータ種別
 * @graph-connects none
 */
export const APPROVAL_KINDS = ['term', 'actor', 'domain', 'stage'];
/** 承認の対象になるデータ種別。 @graph-connects none */
export const ApprovalKindSchema = z.enum(APPROVAL_KINDS);
/**
 * ノード型から一覧の種別への対応。API が承認の一覧（`listTermChangesAcrossPjs`）の行に種別を
 * 付けるときに引く。種別ごとに 1 型で、型を 1 つの種別へ潰さない。
 * @graph-connects none
 */
export const APPROVAL_KIND_BY_NODE_TYPE = {
    Term: 'term',
    Actor: 'actor',
    Domain: 'domain',
    Stage: 'stage',
};
/**
 * `getApprovalAccess` の入力。項目を 1 つも取らない。
 *
 * 空でも宣言するのは、契約側の「すべての procedure が入力スキーマを持つ」を崩さないためである。
 * @graph-connects none
 */
export const GetApprovalAccessInputSchema = z.object({});
/**
 * `getApprovalAccess` の出力。**呼び出した本人が承認を積める担当者か**だけを返す。
 *
 * サイドバーの「承認一覧」を担当者にだけ出すために引く。ロールの照合だけで答え、案件の状態は
 * 見ない — 画面の入口を出すかどうかに案件は関係しない。`auth.me` に足さないのは、権限が案件の
 * 操作の関心であって利用者の属性ではないからである。
 * @graph-connects none
 */
export const GetApprovalAccessOutputSchema = z.object({
    canApprove: z.boolean(),
});
/**
 * 旧表を相手にした承認（撤去済み）で 1 回の呼び出しに積めた変更行の上限。作り直しの承認は
 * `APPROVAL_BATCH_LIMIT` を使う。画面のテストが参照しているあいだ残す。
 *
 * 承認は行ごとに判断する操作で、PJ の承認対象の変更が数百件になる場面は無い（用語の初期登録が
 * 約 87 語、ステージは 10〜30 件、アクターは 5〜10 件）。上限が無いと画面の
 * バグ 1 つで際限なく行を積める。
 * @graph-connects none
 */
export const TERM_APPROVAL_BATCH_LIMIT = 200;
//# sourceMappingURL=spec-pj-term-approval.js.map